import { Alert, Card, Center, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import { forwardRef, RefObject, useImperativeHandle, useMemo, useState } from "react";
import NoData from "@/components/no-data";
import Table from "@/components/table";
import { THandler } from "@/components/table/Table";
import TablePagination from "@/components/table/TablePagination";
import licenciadaColumns from "./Columns";
import { FiltroLicenciada } from "./ModalFilterLicenciadas";
import { DEFAULT_SORTING, getPaginatedLicenciadas } from "../../firestore";

export const QUERY_KEY: string = "licenciadas-list";

interface LicenciadasTableProperties {
  ref: RefObject<THandler<TLicenciada>>;
  filter?: FiltroLicenciada;
}

/**
 * Apply client-side filters to licenciadas
 * @param licenciadas - List of licenciadas to filter
 * @param filter - Filter criteria
 * @returns Filtered licenciadas
 */
function aplicarFiltros(licenciadas: TLicenciada[], filter: FiltroLicenciada): TLicenciada[] {
  if (!filter.search || !filter.campo) {
    return licenciadas;
  }

  const searchTerm = filter.search.toLowerCase();

  return licenciadas.filter((item) => {
    switch (filter.campo) {
      case "razaoSocial": {
        return item.razaoSocial?.toLowerCase().includes(searchTerm) ?? false;
      }
      case "cnpj": {
        return item.cnpj?.toLowerCase().includes(searchTerm) ?? false;
      }
      case "responsavel": {
        return item.responsavel?.nome?.toLowerCase().includes(searchTerm) ?? false;
      }
      case "cidade": {
        return item.endereco?.cidade?.toLowerCase().includes(searchTerm) ?? false;
      }
      case "uf": {
        return item.endereco?.estado?.toLowerCase().includes(searchTerm) ?? false;
      }
      default: {
        return true;
      }
    }
  });
}

const LicenciadasTable = forwardRef<THandler<TLicenciada>, LicenciadasTableProperties>(({ filter }, reference) => {
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  // Fetch all licenciadas without server-side filtering
  const {
    data: queryData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () =>
      getPaginatedLicenciadas({
        sorting: DEFAULT_SORTING
      })
  });

  // Extract licenciadas from query result
  const allLicenciadas = useMemo(() => {
    if (!queryData) return [];
    const [snapshot] = queryData;
    return snapshot.docs.map((document_) => document_.data());
  }, [queryData]);

  // Apply client-side filters
  const filteredData = useMemo(() => {
    if (!filter) return allLicenciadas;
    return aplicarFiltros(allLicenciadas, filter);
  }, [allLicenciadas, filter]);

  const table = useReactTable({
    data: filteredData,
    columns: licenciadaColumns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  // Create tableHandlers for TablePagination
  const tableHandlers: THandler<TLicenciada> = useMemo(
    () => ({
      table,
      data: {
        entries: filteredData,
        firstDocument: undefined,
        lastDocument: undefined,
        total: filteredData.length
      },
      isLoading,
      error: error,
      filters: []
    }),
    [table, filteredData, isLoading, error]
  );

  useImperativeHandle<THandler<TLicenciada>, THandler<TLicenciada>>(reference, () => tableHandlers, [tableHandlers]);

  /**
   * Handles the close of the error alert.
   */
  function handleErrorClose() {
    void refetch();
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title="Erro ao buscar licenciadas"
        color="red"
        withCloseButton
        onClose={handleErrorClose}
      >
        {error.message}
      </Alert>
    );
  }

  const isEmpty = !isLoading && filteredData.length === 0;

  return (
    <>
      <Card.Section withBorder>
        <Table table={table} isLoading={isLoading} />
        {isEmpty && !isLoading && (
          <Center m="lg">
            <NoData m="md">
              <Stack gap="xs">
                <Text c="dimmed">
                  {filter?.search
                    ? "Nenhuma licenciada encontrada com o filtro aplicado!"
                    : "Nenhuma Licenciada Cadastrada!"}
                </Text>
              </Stack>
            </NoData>
          </Center>
        )}
      </Card.Section>
      <Card.Section withBorder>
        <TablePagination tableHandlers={tableHandlers} />
      </Card.Section>
    </>
  );
});

LicenciadasTable.displayName = "LicenciadasTable";

export default LicenciadasTable;
